// Single home for the "documents per order" read model of the Dokumenty tab.
//
// Every Document Center row is attached to at most one order: a customer
// order (WZ, sales invoice) or a supplier order (PZ via its source order,
// purchase invoice). Rows without either land in a trailing "no order"
// group. Groups are ordered by their newest document so recent work stays on
// top; documents inside a group keep the backend order (newest first).
import type { DocumentCenterItem } from './api/documentCenterApi';

export type DocumentCenterGroupKind = 'CUSTOMER_ORDER' | 'SUPPLIER_ORDER' | 'NONE';

export interface DocumentCenterGroup {
  key: string;
  kind: DocumentCenterGroupKind;
  /** Route of the order the group belongs to; null for the no-order group. */
  orderPath: string | null;
  /** Operator-facing order number; null for the no-order group. */
  orderNumber: string | null;
  clientOrderNumber: string | null;
  /** Customer (customer order) or supplier (supplier order) name. */
  partyName: string | null;
  documents: DocumentCenterItem[];
  /** Newest document date in the group, ISO string or null when unknown. */
  latestDate: string | null;
}

const NO_ORDER_KEY = 'NONE';

const documentTime = (document: DocumentCenterItem): number => {
  const raw = document.date || document.createdAt;
  const time = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

const resolveGroupSeed = (document: DocumentCenterItem): Pick<DocumentCenterGroup, 'key' | 'kind' | 'orderPath' | 'orderNumber' | 'clientOrderNumber' | 'partyName'> => {
  if (document.orderId) {
    return {
      key: `CUSTOMER_ORDER:${document.orderId}`,
      kind: 'CUSTOMER_ORDER',
      orderPath: `/app/orders/${document.orderId}`,
      orderNumber: document.displayOrderNumber || document.orderNumber || document.clientOrderNumber || document.orderId,
      clientOrderNumber: document.clientOrderNumber || null,
      partyName: document.customerName || null,
    };
  }
  if (document.supplierOrderId) {
    return {
      key: `SUPPLIER_ORDER:${document.supplierOrderId}`,
      kind: 'SUPPLIER_ORDER',
      orderPath: `/app/supplier-orders/${document.supplierOrderId}`,
      orderNumber: document.supplierOrderNumber || document.displayOrderNumber || document.orderNumber || document.supplierOrderId,
      clientOrderNumber: document.clientOrderNumber || null,
      partyName: document.supplierName || null,
    };
  }
  return {
    key: NO_ORDER_KEY,
    kind: 'NONE',
    orderPath: null,
    orderNumber: null,
    clientOrderNumber: null,
    partyName: null,
  };
};

export function groupDocumentsByOrder(documents: DocumentCenterItem[]): DocumentCenterGroup[] {
  const groups = new Map<string, DocumentCenterGroup>();

  documents.forEach((document) => {
    const seed = resolveGroupSeed(document);
    const existing = groups.get(seed.key);
    if (existing) {
      existing.documents.push(document);
      // Fill party/order labels from a later row when the first row lacked them.
      if (!existing.partyName && seed.partyName) existing.partyName = seed.partyName;
      if (!existing.clientOrderNumber && seed.clientOrderNumber) existing.clientOrderNumber = seed.clientOrderNumber;
      return;
    }
    groups.set(seed.key, { ...seed, documents: [document], latestDate: null });
  });

  const result = Array.from(groups.values()).map((group) => {
    const newest = group.documents.reduce((max, document) => Math.max(max, documentTime(document)), 0);
    return { ...group, latestDate: newest ? new Date(newest).toISOString() : null };
  });

  return result.sort((a, b) => {
    if (a.kind === 'NONE') return 1;
    if (b.kind === 'NONE') return -1;
    const byDate = (b.latestDate ? new Date(b.latestDate).getTime() : 0) - (a.latestDate ? new Date(a.latestDate).getTime() : 0);
    if (byDate !== 0) return byDate;
    return (a.orderNumber || '').localeCompare(b.orderNumber || '', 'pl');
  });
}

/**
 * Total value of a group, shown next to the document count on the collapsed
 * header. Returns null when the group has no amounts at all or mixes
 * currencies — summing across currencies would state a number that is not a
 * real amount of money.
 */
export function sumDocumentCenterGroupAmount(
  group: DocumentCenterGroup
): { amount: number; currency: string } | null {
  let currency: string | null = null;
  let total = 0;
  let counted = 0;

  for (const document of group.documents) {
    if (document.amount == null) continue;
    const value = Number(document.amount);
    if (!Number.isFinite(value)) continue;
    const documentCurrency = document.currency || 'PLN';
    if (currency === null) currency = documentCurrency;
    else if (currency !== documentCurrency) return null;
    total += value;
    counted += 1;
  }

  return counted > 0 && currency ? { amount: total, currency } : null;
}

/**
 * Document path of a group: the order itself, then the operational steps that
 * follow it — receipt/issue, then the invoice. Supplier orders run
 * `ZD → PZ → FV`, customer orders `ZK → WZ → FS`; the no-order group has no
 * path at all. A step is always listed, with `count: 0` when the document has
 * not been issued yet, so the chain reads as a fixed path with gaps rather
 * than a variable-length list. Purely descriptive: the path carries no amounts
 * and is visible to every role.
 */
export type DocumentCenterPathStepType = 'PZ' | 'WZ' | 'SALES_INVOICE' | 'PURCHASE_INVOICE';

export interface DocumentCenterPathStep {
  type: DocumentCenterPathStepType;
  count: number;
  /** How many documents of the step carry each status; empty when none do. */
  statuses: Record<string, number>;
  /** True when any document of the step follows the invoice workflow, which
   *  decides how its status is labelled. */
  workflow: boolean;
}

export interface DocumentCenterGroupPath {
  /** Short operator code of the order itself; null for the no-order group. */
  order: 'ZD' | 'ZK' | null;
  steps: DocumentCenterPathStep[];
}

const PATH_STEPS: Record<DocumentCenterGroupKind, { order: 'ZD' | 'ZK' | null; steps: DocumentCenterPathStepType[] }> = {
  SUPPLIER_ORDER: { order: 'ZD', steps: ['PZ', 'PURCHASE_INVOICE'] },
  CUSTOMER_ORDER: { order: 'ZK', steps: ['WZ', 'SALES_INVOICE'] },
  NONE: { order: null, steps: [] },
};

export function summarizeDocumentCenterGroupPath(group: DocumentCenterGroup): DocumentCenterGroupPath {
  const shape = PATH_STEPS[group.kind];

  const steps = shape.steps.map<DocumentCenterPathStep>((type) => {
    const documents = group.documents.filter((document) => document.type === type);
    const statuses: Record<string, number> = {};
    documents.forEach((document) => {
      if (!document.status) return;
      statuses[document.status] = (statuses[document.status] || 0) + 1;
    });
    return {
      type,
      count: documents.length,
      statuses,
      workflow: documents.some((document) => Boolean(document.workflow)),
    };
  });

  return { order: shape.order, steps };
}
